import { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid2 as Grid } from '@mui/material';
import { ControlledShow } from 'framework/show';
import { Loading } from 'framework/ui/mui-builder';
import { ShowSection } from 'framework/ui/layout';
import { useAuthenticationProvider } from 'infrastructure/security/authentication/AuthenticationProvider';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectDataContext,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { useProjectId } from 'domain/shared/project';
import { AssumptionActionsProvider } from './CreateAndUpdateAssumption/AssumptionActionsProvider';
import AssumptionsList from './List/AssumptionsList';
import { GET_PROJECT_ASSUMPTION } from './graphql/assumptionQueries';
import { useQuery } from '@apollo/client/react';
import { AssumptionType, ProjectAssumptionResponse } from './types';

const AssumptionsContext = () => {
  const id = useProjectId();
  const projectId = typeof id === 'string' ? id : String(id);
  const { data, loading } = useQuery<ProjectAssumptionResponse>(GET_PROJECT_ASSUMPTION, {
    variables: { id: projectId.toString() },
    skip: !projectId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const { isAccessGranted } = useProjectSecurityContext();
  const { data: projectData } = useProjectDataContext();
  const { getUserAlias } = useAuthenticationProvider();

  const hasManage = isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.MANAGE);
  const hasReview = isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.REVIEW);
  const hasPartialManage = isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.PARTIAL_MANAGE);

  const userTeamRoles = useMemo(() => {
    const alias = getUserAlias();
    if (!alias) return [] as string[];
    return projectData?.team?.members?.filter(m => m.employeeAlias === alias).map(m => m.role) ?? [];
  }, [projectData, getUserAlias]);

  const [localAssumptions, setLocalAssumptions] = useState<AssumptionType[] | null>(null);

  useEffect(() => {
    setLocalAssumptions(null);
  }, [data?.baseProject]);

  const upsertAssumption = useCallback(
    (saved: AssumptionType) => {
      setLocalAssumptions(prev => {
        const current = prev ?? data?.baseProject?.assumptions ?? [];
        const index = current.findIndex(item => item.id === saved.id);
        return index === -1 ? [...current, saved] : current.map(item => (item.id === saved.id ? saved : item));
      });
    },
    [data],
  );

  const visibleAssumptions = useMemo(() => {
    const assumptions = localAssumptions ?? data?.baseProject?.assumptions ?? [];
    const seesAll = hasManage || hasReview;
    return assumptions.filter(assumption => {
      if (seesAll) return true;
      if (hasPartialManage && assumption.assignedRole && userTeamRoles.includes(assumption.assignedRole)) {
        return true;
      }
      return false;
    });
  }, [localAssumptions, data, hasManage, hasReview, hasPartialManage, userTeamRoles]);

  if (loading) {
    return <Loading isInitialPosition />;
  }

  return (
    <ShowSection sx={{ '&.section-details__wrapper': { p: '0px !important' } }}>
      <ControlledShow data={{ assumptions: visibleAssumptions }}>
        <Grid size={12}>
          <AssumptionActionsProvider projectId={projectId} onSaved={upsertAssumption}>
            <AssumptionsList />
          </AssumptionActionsProvider>
        </Grid>
      </ControlledShow>
    </ShowSection>
  );
};

export default AssumptionsContext;
